import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TeamMatchesResponse } from '../services/scheduleService';

interface CacheEntry {
  data: TeamMatchesResponse;
  timestamp: number;
  expiresAt: number;
}

interface TeamMatchCacheState {
  cache: Record<string, CacheEntry>; // key: leagueId:teamName:page
  maxSize: number;
  ttlMs: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50;

const initialState: TeamMatchCacheState = {
  cache: {},
  maxSize: MAX_CACHE_SIZE,
  ttlMs: CACHE_TTL_MS,
};

// Helper function to create cache key
const createCacheKey = (leagueId: string, teamName: string, page: number = 1): string => {
  return `${leagueId}:${teamName}:${page}`;
};

// Helper function to check if cache entry is expired
const isExpired = (entry: CacheEntry): boolean => {
  return Date.now() > entry.expiresAt;
};

// Helper function to implement LRU eviction
const evictLRU = (cache: Record<string, CacheEntry>): Record<string, CacheEntry> => {
  const entries = Object.entries(cache);
  if (entries.length <= MAX_CACHE_SIZE) {
    return cache;
  }

  // Sort by timestamp (oldest first) and remove oldest entries
  entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
  const entriesToKeep = entries.slice(entries.length - MAX_CACHE_SIZE);
  
  return Object.fromEntries(entriesToKeep);
};

const teamMatchCacheSlice = createSlice({
  name: 'teamMatchCache',
  initialState,
  reducers: {
    setCacheEntry: (
      state,
      action: PayloadAction<{
        leagueId: string;
        teamName: string;
        page: number;
        data: TeamMatchesResponse;
      }>
    ) => {
      const { leagueId, teamName, page, data } = action.payload;
      const key = createCacheKey(leagueId, teamName, page);
      const now = Date.now();

      state.cache[key] = {
        data,
        timestamp: now,
        expiresAt: now + state.ttlMs,
      };

      // Implement LRU eviction if cache is full
      state.cache = evictLRU(state.cache);
    },

    getCacheEntry: (
      _state,
      _action: PayloadAction<{
        leagueId: string;
        teamName: string;
        page: number;
      }>
    ) => {
      // This is a query action - the actual logic is in the selector
      // We include it here for consistency but the real work is done by selectors
    },

    clearExpiredEntries: (state) => {
      Object.keys(state.cache).forEach(key => {
        if (isExpired(state.cache[key])) {
          delete state.cache[key];
        }
      });
    },

    clearCacheForLeague: (state, action: PayloadAction<string>) => {
      const leagueId = action.payload;
      Object.keys(state.cache).forEach(key => {
        if (key.startsWith(`${leagueId}:`)) {
          delete state.cache[key];
        }
      });
    },

    clearAllCache: (state) => {
      state.cache = {};
    },

    clearCacheForTeam: (
      state,
      action: PayloadAction<{ leagueId: string; teamName: string }>
    ) => {
      const { leagueId, teamName } = action.payload;
      const prefix = `${leagueId}:${teamName}:`;
      Object.keys(state.cache).forEach(key => {
        if (key.startsWith(prefix)) {
          delete state.cache[key];
        }
      });
    },
  },
});

export const {
  setCacheEntry,
  getCacheEntry,
  clearExpiredEntries,
  clearCacheForLeague,
  clearAllCache,
  clearCacheForTeam,
} = teamMatchCacheSlice.actions;

// Selectors
export const selectCacheEntry = (
  state: { teamMatchCache: TeamMatchCacheState },
  leagueId: string,
  teamName: string,
  page: number = 1
): CacheEntry | null => {
  const key = createCacheKey(leagueId, teamName, page);
  const entry = state.teamMatchCache.cache[key];
  
  if (!entry || isExpired(entry)) {
    return null;
  }
  
  return entry;
};

export const selectCacheStats = (state: { teamMatchCache: TeamMatchCacheState }) => {
  const cache = state.teamMatchCache.cache;
  const totalEntries = Object.keys(cache).length;
  const expiredEntries = Object.values(cache).filter(isExpired).length;
  const validEntries = totalEntries - expiredEntries;
  
  return {
    totalEntries,
    validEntries,
    expiredEntries,
    maxSize: state.teamMatchCache.maxSize,
    ttlMs: state.teamMatchCache.ttlMs,
  };
};

export default teamMatchCacheSlice.reducer;
